/**
 * Cache Invalidation Utilities
 * 
 * This module provides standardized cache invalidation patterns for real-time updates
 * across the application. It ensures that when data is created, updated, or deleted,
 * all related queries are properly invalidated to reflect changes immediately.
 */

/**
 * Invalidate all user-related queries
 * @param {QueryClient} queryClient - React Query client instance
 */
export const invalidateUserQueries = (queryClient) => {
  queryClient.invalidateQueries(['users'])
  queryClient.invalidateQueries(['users', 'all'])
  // Also invalidate any specific user queries that might be cached
  queryClient.invalidateQueries({ queryKey: ['user'], exact: false })
}

/**
 * Invalidate all brand-related queries
 * @param {QueryClient} queryClient - React Query client instance
 */
export const invalidateBrandQueries = (queryClient) => {
  queryClient.invalidateQueries(['brands'])
  queryClient.invalidateQueries(['brands', 'all'])
  queryClient.invalidateQueries('brands-for-user')
  queryClient.invalidateQueries('brands-for-user-edit')
  // Also invalidate any specific brand queries that might be cached
  queryClient.invalidateQueries({ queryKey: ['brand'], exact: false })
}

/**
 * Invalidate all data input related queries
 * @param {QueryClient} queryClient - React Query client instance
 * @param {number} dataInputId - Optional specific data input ID
 */
export const invalidateDataInputQueries = (queryClient, dataInputId = null) => {
  queryClient.invalidateQueries(['dataInputs'])
  queryClient.invalidateQueries(['dataInputs', 'all'])
  
  if (dataInputId) {
    queryClient.invalidateQueries(['dataInput', dataInputId])
    queryClient.invalidateQueries(['dataInput', dataInputId.toString()])
  } else {
    // Invalidate all data input detail queries
    queryClient.invalidateQueries({ queryKey: ['dataInput'], exact: false })
  }
}

/**
 * Invalidate all record-related queries for a specific data input
 * @param {QueryClient} queryClient - React Query client instance
 * @param {number} dataInputId - Data input ID
 */
export const invalidateRecordQueries = (queryClient, dataInputId) => {
  // Invalidate the parent data input to refresh record counts
  queryClient.invalidateQueries(['dataInput', dataInputId])
  queryClient.invalidateQueries(['dataInput', dataInputId.toString()])
  
  // Invalidate any record-specific queries
  queryClient.invalidateQueries({ queryKey: ['records', dataInputId], exact: false })
  queryClient.invalidateQueries({ queryKey: ['record', dataInputId], exact: false })
}

/**
 * Invalidate all attribute-related queries for a specific data input
 * @param {QueryClient} queryClient - React Query client instance
 * @param {number} dataInputId - Data input ID
 */
export const invalidateAttributeQueries = (queryClient, dataInputId) => {
  // Invalidate the parent data input to refresh attribute data
  queryClient.invalidateQueries(['dataInput', dataInputId])
  queryClient.invalidateQueries(['dataInput', dataInputId.toString()])
  
  // Invalidate any attribute-specific queries
  queryClient.invalidateQueries({ queryKey: ['attributes', dataInputId], exact: false })
  queryClient.invalidateQueries({ queryKey: ['attribute', dataInputId], exact: false })
}

/**
 * Invalidate all permission-related queries
 * @param {QueryClient} queryClient - React Query client instance
 */
export const invalidatePermissionQueries = (queryClient) => {
  queryClient.invalidateQueries(['permissions'])
  queryClient.invalidateQueries(['roles'])
  // Also invalidate user queries since permissions might affect user data
  invalidateUserQueries(queryClient)
}

/**
 * Comprehensive cache invalidation for major operations
 * Use this when an operation might affect multiple entities
 * @param {QueryClient} queryClient - React Query client instance
 */
export const invalidateAllQueries = (queryClient) => {
  invalidateUserQueries(queryClient)
  invalidateBrandQueries(queryClient)
  invalidateDataInputQueries(queryClient)
  invalidatePermissionQueries(queryClient)
}

/**
 * Smart cache invalidation based on operation type and entity
 * @param {QueryClient} queryClient - React Query client instance
 * @param {string} entity - Entity type ('user', 'brand', 'dataInput', 'record', 'attribute')
 * @param {string} operation - Operation type ('create', 'update', 'delete')
 * @param {number|string} entityId - Optional entity ID for specific invalidations
 */
export const smartCacheInvalidation = (queryClient, entity, operation, entityId = null) => {
  switch (entity) {
    case 'user':
      invalidateUserQueries(queryClient)
      // User changes might affect brand access, so invalidate brands too
      if (operation === 'update' || operation === 'delete') {
        invalidateBrandQueries(queryClient)
      }
      break
      
    case 'brand':
      invalidateBrandQueries(queryClient)
      // Brand changes might affect user access, so invalidate users too
      invalidateUserQueries(queryClient)
      // Brand changes might affect data inputs
      invalidateDataInputQueries(queryClient)
      break
      
    case 'dataInput':
      invalidateDataInputQueries(queryClient, entityId)
      // Data input changes might affect brand relationships
      invalidateBrandQueries(queryClient)
      break
      
    case 'record':
      if (entityId) {
        invalidateRecordQueries(queryClient, entityId)
      }
      break
      
    case 'attribute':
      if (entityId) {
        invalidateAttributeQueries(queryClient, entityId)
      }
      break
      
    case 'permission':
      invalidatePermissionQueries(queryClient)
      break
      
    default:
      console.warn(`Unknown entity type for cache invalidation: ${entity}`)
      break
  }
}

/**
 * Optimistic update helper
 * Updates cache optimistically and provides rollback functionality
 * @param {QueryClient} queryClient - React Query client instance
 * @param {string|Array} queryKey - Query key to update
 * @param {Function} updateFn - Function to update the cached data
 * @returns {Function} Rollback function to revert changes
 */
export const optimisticUpdate = (queryClient, queryKey, updateFn) => {
  // Get the current data
  const previousData = queryClient.getQueryData(queryKey)
  
  // Optimistically update the cache
  queryClient.setQueryData(queryKey, updateFn)
  
  // Return rollback function
  return () => {
    queryClient.setQueryData(queryKey, previousData)
  }
}

/**
 * Batch cache operations for better performance
 * @param {QueryClient} queryClient - React Query client instance
 * @param {Function} operations - Function containing cache operations
 */
export const batchCacheOperations = (queryClient, operations) => {
  queryClient.getQueryCache().batch(() => {
    operations(queryClient)
  })
}
